import React, { useMemo, useState } from 'react';
import { EcxDateRangePicker } from '../common/EcxDatePicker';
import { useEyeconxStore } from '../../store/eyeconxStore';
import patientService from '../../services/patient.service';
import './PatientListStep.css';

const SHARED_SEARCH_FIELDS = [
    { value: 'fullName', label: 'Full Name', placeholder: 'Search first and last name...' },
    { value: 'firstName', label: 'First Name', placeholder: 'Search first name...' },
    { value: 'lastName', label: 'Last Name', placeholder: 'Search last name...' },
];

const SMS_SEARCH_FIELDS = [
    ...SHARED_SEARCH_FIELDS,
    { value: 'cellPhone', label: 'Cell Phone', placeholder: 'Search cell phone number...' },
];

const EMAIL_SEARCH_FIELDS = [
    ...SHARED_SEARCH_FIELDS,
    { value: 'email', label: 'Email', placeholder: 'Search email address...' },
];

const DEFAULT_FILTERS = {
    searchField: 'fullName',
    searchTerm: '',
    todaysAppointments: false,
    dateFrom: '',
    dateTo: '',
    gender: '',
    minAge: '',
    maxAge: '',
};

const escapeRsqlValue = (value) => String(value).replace(/([\\*,])/g, '\\$1');

const toLocalDateString = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const parseOptionalAge = (value) => {
    if (value === '' || value == null) {
        return null;
    }
    const parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : NaN;
};

export const validateCampaignPatientFilters = (filters) => {
    const minAge = parseOptionalAge(filters.minAge);
    const maxAge = parseOptionalAge(filters.maxAge);

    if (filters.minAge !== '' && Number.isNaN(minAge)) {
        return 'Min age must be a whole number.';
    }
    if (filters.maxAge !== '' && Number.isNaN(maxAge)) {
        return 'Max age must be a whole number.';
    }
    if (minAge != null && (minAge < 5 || minAge > 120)) {
        return 'Min age must be between 5 and 120.';
    }
    if (maxAge != null && (maxAge < 5 || maxAge > 120)) {
        return 'Max age must be between 5 and 120.';
    }
    if (minAge != null && maxAge != null && minAge > maxAge) {
        return 'Min age must be less than or equal to max age.';
    }
    if (filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo) {
        return 'Appointment from date must be on or before the to date.';
    }
    return '';
};

export const buildCampaignPatientRsql = (filters, clinicToday) => {
    const parts = [];

    const term = filters.searchTerm?.trim();
    if (term) {
        const field = filters.searchField || 'fullName';
        parts.push(`${field}==*${escapeRsqlValue(term)}*`);
    }

    if (filters.gender) {
        parts.push(`gender==${escapeRsqlValue(filters.gender)}`);
    }

    if (filters.minAge) {
        parts.push(`minAge==${escapeRsqlValue(filters.minAge)}`);
    }

    if (filters.maxAge) {
        parts.push(`maxAge==${escapeRsqlValue(filters.maxAge)}`);
    }

    let appointmentFrom = filters.dateFrom;
    let appointmentTo = filters.dateTo;

    if (filters.todaysAppointments && clinicToday) {
        appointmentFrom = clinicToday;
        appointmentTo = clinicToday;
    }

    if (appointmentFrom) {
        parts.push(`appointmentFrom==${escapeRsqlValue(appointmentFrom)}`);
    }

    if (appointmentTo) {
        parts.push(`appointmentTo==${escapeRsqlValue(appointmentTo)}`);
    }

    return parts.join(',');
};

const getSearchFields = (contactField) =>
    contactField === 'email' ? EMAIL_SEARCH_FIELDS : SMS_SEARCH_FIELDS;

const getSearchPlaceholder = (searchFields, searchField) =>
    searchFields.find((field) => field.value === searchField)?.placeholder || 'Search patients...';

const buildFilterChips = (filters, clinicToday, searchFields) => {
    const chips = [];

    if (filters.searchTerm.trim()) {
        const fieldLabel =
            searchFields.find((field) => field.value === filters.searchField)?.label || 'Search';
        chips.push({
            key: 'search',
            label: `${fieldLabel}: ${filters.searchTerm.trim()}`,
        });
    }

    if (filters.todaysAppointments) {
        chips.push({
            key: 'todaysAppointments',
            label: `Today's appointments${clinicToday ? ` (${clinicToday})` : ''}`,
        });
    }

    if (!filters.todaysAppointments && (filters.dateFrom || filters.dateTo)) {
        const from = filters.dateFrom || '…';
        const to = filters.dateTo || '…';
        chips.push({ key: 'dateRange', label: `Appt date: ${from} → ${to}` });
    }

    if (filters.gender) {
        chips.push({ key: 'gender', label: `Gender: ${filters.gender}` });
    }

    if (filters.minAge || filters.maxAge) {
        const min = filters.minAge || '5';
        const max = filters.maxAge || '120';
        chips.push({ key: 'ageRange', label: `Age: ${min}–${max}` });
    }

    return chips;
};

const formatPatientName = (patient) => {
    const fullName = [patient.firstName, patient.lastName].filter(Boolean).join(' ').trim();
    if (fullName) {
        return fullName;
    }
    return patient.preferredName || 'Unknown';
};

const formatPhoneDisplay = (value) => {
    if (value == null || value === '') {
        return '—';
    }
    const digits = String(value).replace(/\D/g, '');
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return String(value);
};

const toSelectedPatient = (patient) => ({
    id: patient.id,
    firstName: patient.firstName || '',
    lastName: patient.lastName || '',
    preferredName: patient.preferredName || '',
    name: formatPatientName(patient),
    phone: patient.cellPhone || patient.phone || '',
    email: patient.email || '',
});

function PatientListStep({
    formData,
    setFormData,
    contactField = 'phone',
    patientCampaignType = 'blast',
    entityLabel = 'blast',
}) {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
    const [searchResults, setSearchResults] = useState([]);
    const [resultTotal, setResultTotal] = useState(0);
    const [checkedIds, setCheckedIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [hasSearched, setHasSearched] = useState(false);

    const clinicToday = useMemo(() => toLocalDateString(), []);
    const searchFields = useMemo(() => getSearchFields(contactField), [contactField]);
    const mediumType = contactField === 'email' ? 'email' : 'sms';

    const activeChips = useMemo(
        () => buildFilterChips(appliedFilters, clinicToday, searchFields),
        [appliedFilters, clinicToday, searchFields]
    );
    const pendingChanges = useMemo(
        () => JSON.stringify(filters) !== JSON.stringify(appliedFilters),
        [filters, appliedFilters]
    );

    const selectableResults = useMemo(
        () =>
            searchResults.filter(
                (patient) => !formData.selectedPatients.some((selected) => selected.id === patient.id)
            ),
        [searchResults, formData.selectedPatients]
    );

    const allSelectableChecked =
        selectableResults.length > 0 &&
        selectableResults.every((patient) => checkedIds.includes(patient.id));

    const someSelectableChecked =
        selectableResults.some((patient) => checkedIds.includes(patient.id)) && !allSelectableChecked;

    const checkedCount = checkedIds.length;

    const updateFilter = (field, value) => {
        setFilters((prev) => {
            const next = { ...prev, [field]: value };
            if (field === 'todaysAppointments' && value) {
                next.dateFrom = '';
                next.dateTo = '';
            }
            if ((field === 'dateFrom' || field === 'dateTo') && value) {
                next.todaysAppointments = false;
            }
            return next;
        });
    };

    const runSearch = async (nextFilters) => {
        if (!clinicId) {
            return;
        }

        const validationError = validateCampaignPatientFilters(nextFilters);
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        setError('');
        setHasSearched(true);

        try {
            const query = buildCampaignPatientRsql(nextFilters, clinicToday);
            const response = await patientService.getCampaignPatients(clinicId, {
                pageNo: 0,
                pageSize: 50,
                query,
                mediumType,
                type: patientCampaignType,
            });
            const content = response.data?.content || [];
            setSearchResults(content);
            setCheckedIds([]);
            setResultTotal(
                typeof response.data?.totalElements === 'number'
                    ? response.data.totalElements
                    : content.length
            );
            setAppliedFilters({ ...nextFilters });
        } catch (err) {
            console.error('Failed to search campaign patients', err);
            setSearchResults([]);
            setCheckedIds([]);
            setResultTotal(0);
            setError(err?.response?.data?.errorMessage || 'Could not search patients.');
        } finally {
            setLoading(false);
        }
    };

    const handleApplyFilters = () => {
        runSearch(filters);
    };

    const handleClearFilters = () => {
        setFilters(DEFAULT_FILTERS);
        setAppliedFilters(DEFAULT_FILTERS);
        setSearchResults([]);
        setCheckedIds([]);
        setResultTotal(0);
        setHasSearched(false);
        setError('');
    };

    const handleRemoveChip = (chipKey) => {
        const next = { ...appliedFilters };

        switch (chipKey) {
            case 'search':
                next.searchTerm = '';
                break;
            case 'todaysAppointments':
                next.todaysAppointments = false;
                break;
            case 'dateRange':
                next.dateFrom = '';
                next.dateTo = '';
                break;
            case 'gender':
                next.gender = '';
                break;
            case 'ageRange':
                next.minAge = '';
                next.maxAge = '';
                break;
            default:
                break;
        }

        setFilters(next);
        runSearch(next);
    };

    const isSelected = (patientId) =>
        formData.selectedPatients.some((patient) => patient.id === patientId);

    const isChecked = (patientId) => checkedIds.includes(patientId);

    const handleTogglePatient = (patientId) => {
        if (isSelected(patientId)) {
            return;
        }

        setCheckedIds((prev) =>
            prev.includes(patientId) ? prev.filter((id) => id !== patientId) : [...prev, patientId]
        );
    };

    const handleToggleAll = () => {
        if (allSelectableChecked) {
            setCheckedIds([]);
            return;
        }

        setCheckedIds(selectableResults.map((patient) => patient.id));
    };

    const handleAddCheckedPatients = () => {
        if (checkedIds.length === 0) {
            return;
        }

        const existingIds = new Set(formData.selectedPatients.map((patient) => patient.id));
        const toAdd = searchResults
            .filter((patient) => checkedIds.includes(patient.id) && !existingIds.has(patient.id))
            .map(toSelectedPatient);

        if (toAdd.length === 0) {
            setCheckedIds([]);
            return;
        }

        setFormData({
            ...formData,
            selectedPatients: [...formData.selectedPatients, ...toAdd],
        });
        setCheckedIds([]);
    };

    const handleRemovePatient = (index) => {
        const nextPatients = [...formData.selectedPatients];
        nextPatients.splice(index, 1);
        setFormData({ ...formData, selectedPatients: nextPatients });
    };

    const handleClearSelected = () => {
        setFormData({ ...formData, selectedPatients: [] });
    };

    const contactLabel = contactField === 'email' ? 'Email' : 'Cell Phone';
    const displayContact = (patient) =>
        contactField === 'email'
            ? patient.email || '—'
            : formatPhoneDisplay(patient.cellPhone || patient.phone);

    return (
        <div className="animate-fade-in ecx-patient-list">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
                <div>
                    <h3 className="fw-normal text-dark mb-1">Patient List</h3>
                    <p className="text-muted mb-0">
                        Search and filter patients, then add them to this{' '}
                        {entityLabel.toLowerCase()}.
                    </p>
                </div>
                <span className="ecx-page-chip">
                    <i className="bi bi-people" />
                    {formData.selectedPatients.length} selected
                </span>
            </div>

            <div className="ecx-patient-list__filters">
                <div className="ecx-patient-list__filters-row">
                    <div className="ecx-patient-list__field ecx-patient-list__field--search-type">
                        <label className="ecx-patient-list__label">
                            <i className="bi bi-search" />
                            Search by
                        </label>
                        <select
                            className="form-select form-select-sm"
                            value={filters.searchField}
                            onChange={(e) => updateFilter('searchField', e.target.value)}
                            disabled={loading}
                        >
                            {searchFields.map((field) => (
                                <option key={field.value} value={field.value}>
                                    {field.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="ecx-patient-list__field ecx-patient-list__field--search-term">
                        <label className="ecx-patient-list__label">
                            <i className="bi bi-type" />
                            Search term
                        </label>
                        <div className="input-group input-group-sm">
                            <input
                                type="search"
                                className="form-control"
                                placeholder={getSearchPlaceholder(searchFields, filters.searchField)}
                                value={filters.searchTerm}
                                onChange={(e) => updateFilter('searchTerm', e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleApplyFilters}
                                disabled={loading}
                            >
                                Search
                            </button>
                        </div>
                    </div>
                </div>

                <div className="ecx-patient-list__filters-row">
                    <div className="ecx-patient-list__field">
                        <label className="ecx-patient-list__label">
                            <i className="bi bi-calendar-check" />
                            Appointments
                        </label>
                        <button
                            type="button"
                            className={`ecx-patient-list__pill ${
                                filters.todaysAppointments ? 'is-active' : ''
                            }`}
                            onClick={() =>
                                updateFilter('todaysAppointments', !filters.todaysAppointments)
                            }
                            disabled={loading}
                        >
                            <i className="bi bi-calendar-day" />
                            Today&apos;s appointments
                        </button>
                    </div>

                    <div className="ecx-patient-list__field ecx-patient-list__field--date">
                        <label className="ecx-patient-list__label">
                            <i className="bi bi-calendar3" />
                            Appointment date range
                        </label>
                        <EcxDateRangePicker
                            startValue={filters.dateFrom}
                            endValue={filters.dateTo}
                            onStartChange={(value) => updateFilter('dateFrom', value)}
                            onEndChange={(value) => updateFilter('dateTo', value)}
                            startPlaceholder="From"
                            endPlaceholder="To"
                            size="sm"
                            disabled={loading || filters.todaysAppointments}
                        />
                    </div>

                    <div className="ecx-patient-list__field">
                        <label className="ecx-patient-list__label">
                            <i className="bi bi-gender-ambiguous" />
                            Gender
                        </label>
                        <select
                            className="form-select form-select-sm"
                            value={filters.gender}
                            onChange={(e) => updateFilter('gender', e.target.value)}
                            disabled={loading}
                        >
                            <option value="">Any</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>

                    <div className="ecx-patient-list__field ecx-patient-list__field--age">
                        <label className="ecx-patient-list__label">
                            <i className="bi bi-hourglass-split" />
                            Age range
                        </label>
                        <div className="ecx-patient-list__age-inputs">
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                placeholder="Min"
                                min="5"
                                max="120"
                                value={filters.minAge}
                                onChange={(e) => updateFilter('minAge', e.target.value)}
                                disabled={loading}
                            />
                            <span className="text-muted">to</span>
                            <input
                                type="number"
                                className="form-control form-control-sm"
                                placeholder="Max"
                                min="5"
                                max="120"
                                value={filters.maxAge}
                                onChange={(e) => updateFilter('maxAge', e.target.value)}
                                disabled={loading}
                            />
                        </div>
                    </div>
                </div>

                <div className="ecx-patient-list__filters-actions">
                    {activeChips.length > 0 && (
                        <div className="ecx-patient-list__chips">
                            {activeChips.map((chip) => (
                                <button
                                    key={chip.key}
                                    type="button"
                                    className="ecx-patient-list__chip"
                                    onClick={() => handleRemoveChip(chip.key)}
                                    disabled={loading}
                                >
                                    {chip.label}
                                    <i className="bi bi-x-lg" />
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="ecx-patient-list__actions ms-auto">
                        <button
                            type="button"
                            className="btn btn-light btn-sm"
                            onClick={handleClearFilters}
                            disabled={loading}
                        >
                            Clear
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={handleApplyFilters}
                            disabled={loading || !pendingChanges}
                        >
                            {loading ? 'Searching...' : 'Apply filters'}
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="alert alert-danger py-2 px-3 mb-3" role="alert">
                    {error}
                </div>
            )}

            <div className="row g-3 mt-1">
                <div className="col-lg-8">
                    <div className="ecx-patient-list__panel">
                        <div className="ecx-patient-list__panel-header">
                            <div>
                                <h6 className="mb-0 fw-normal">Search results</h6>
                                <span className="text-muted small">
                                    {hasSearched
                                        ? `${searchResults.length}${
                                              resultTotal > searchResults.length
                                                  ? ` of ${resultTotal}`
                                                  : ''
                                          } found`
                                        : 'Apply filters to search'}
                                    {checkedCount > 0 ? ` · ${checkedCount} checked` : ''}
                                </span>
                            </div>
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={handleAddCheckedPatients}
                                disabled={loading || checkedCount === 0}
                            >
                                Add selected
                                {checkedCount > 0 ? ` (${checkedCount})` : ''}
                            </button>
                        </div>
                        <div className="ecx-patient-list__panel-body">
                            {loading ? (
                                <div className="ecx-page-empty py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : searchResults.length === 0 ? (
                                <div className="ecx-page-empty py-5">
                                    <span className="ecx-page-empty__icon">
                                        <i className="bi bi-person-lines-fill" />
                                    </span>
                                    <h5 className="mb-2 text-dark">
                                        {hasSearched ? 'No matching patients' : 'No patients loaded yet'}
                                    </h5>
                                    <p className="mb-0 text-muted">
                                        {hasSearched
                                            ? mediumType === 'sms'
                                                ? 'Try adjusting search or filters. Only patients with a cell number who have not opted out of SMS are shown.'
                                                : 'Try adjusting search or filters. Only patients with an email who have not opted out of email are shown.'
                                            : 'Use search and filters above, then apply. Matching patients will appear here for selection.'}
                                    </p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table align-middle mb-0 ecx-patient-list__table">
                                        <thead>
                                            <tr className="text-muted small text-uppercase">
                                                <th className="ecx-patient-list__check-col">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        aria-label="Select all patients"
                                                        checked={allSelectableChecked}
                                                        ref={(input) => {
                                                            if (input) {
                                                                input.indeterminate = someSelectableChecked;
                                                            }
                                                        }}
                                                        onChange={handleToggleAll}
                                                        disabled={selectableResults.length === 0}
                                                    />
                                                </th>
                                                <th>First Name</th>
                                                <th>Last Name</th>
                                                <th>Preferred</th>
                                                <th>{contactLabel}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {searchResults.map((patient) => {
                                                const selected = isSelected(patient.id);
                                                const checked = isChecked(patient.id);
                                                return (
                                                    <tr
                                                        key={patient.id}
                                                        className={
                                                            selected
                                                                ? 'ecx-patient-list__row--added'
                                                                : checked
                                                                  ? 'ecx-patient-list__row--checked'
                                                                  : ''
                                                        }
                                                    >
                                                        <td className="ecx-patient-list__check-col">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                aria-label={`Select ${formatPatientName(patient)}`}
                                                                checked={selected || checked}
                                                                disabled={selected}
                                                                onChange={() => handleTogglePatient(patient.id)}
                                                            />
                                                        </td>
                                                        <td className="fw-normal">{patient.firstName || '—'}</td>
                                                        <td className="fw-normal">{patient.lastName || '—'}</td>
                                                        <td className="text-muted">
                                                            {patient.preferredName || '—'}
                                                        </td>
                                                        <td className="text-muted text-nowrap">
                                                            {displayContact(patient)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-lg-4">
                    <div className="ecx-patient-list__panel ecx-patient-list__panel--selected">
                        <div className="ecx-patient-list__panel-header">
                            <h6 className="mb-0 fw-normal">Selected patients</h6>
                            {formData.selectedPatients.length > 0 && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={handleClearSelected}
                                >
                                    Clear all
                                </button>
                            )}
                        </div>
                        <div className="ecx-patient-list__panel-body">
                            {formData.selectedPatients.length === 0 ? (
                                <div className="ecx-page-empty py-5">
                                    <span className="ecx-page-empty__icon">
                                        <i className="bi bi-person-check" />
                                    </span>
                                    <h5 className="mb-2 text-dark">None selected</h5>
                                    <p className="mb-0 text-muted">
                                        Check patients in search results, then click Add selected.
                                    </p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table align-middle mb-0 ecx-patient-list__table">
                                        <thead>
                                            <tr className="text-muted small text-uppercase">
                                                <th>First</th>
                                                <th>Last</th>
                                                <th>{contactLabel}</th>
                                                <th className="text-end"> </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {formData.selectedPatients.map((patient, index) => (
                                                <tr key={patient.id || index}>
                                                    <td className="fw-normal">
                                                        {patient.firstName || '—'}
                                                    </td>
                                                    <td className="fw-normal">
                                                        {patient.lastName || '—'}
                                                    </td>
                                                    <td className="text-muted text-nowrap">
                                                        {displayContact(patient)}
                                                    </td>
                                                    <td className="text-end">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleRemovePatient(index)}
                                                        >
                                                            Remove
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PatientListStep;
